import { HttpInterceptor, HttpHandler, HttpEvent, HttpErrorResponse, HttpRequest } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, iif, throwError, of } from "rxjs";
import { retryWhen, concatMap, delay } from "rxjs/operators";
import { AppConfigService } from "../services/app-config.service";

export interface RetryRequestOptions {
  maximumRetries: number;
  retryDelay: number;
}

@Injectable()
export class RetryHttpErrorsInterceptor implements HttpInterceptor {
  // default retry options
  private retryRequestOptions: RetryRequestOptions = {
    maximumRetries: 2,
    retryDelay: 200, //milliseconds
  };


  constructor() { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // first time it run: need to replace default retry config options
    return next.handle(request).pipe(this.retryPipe());
  }

  retryPipe<T>() {
    return retryWhen<T>((errors: Observable<HttpErrorResponse>) =>
      errors.pipe(
        // Use concat map to keep the errors in order and make sure they
        // aren't executed in parallel
        concatMap((error: HttpErrorResponse, retryId: number) =>
          // Executes a conditional Observable depending on the result
          // of the first argument
          iif(
            () => retryId >= this.retryRequestOptions.maximumRetries || error.status <= 500,
            // If the condition is true we throw the error (the last error)
            this.handle(error),
            // Otherwise we pipe this back into our stream and delay the retry
            this.retryError(error, retryId, this.retryRequestOptions.retryDelay),
          ),
        ),
      ),
    );
  }

  handle(error: HttpErrorResponse) {
    return throwError(error);
  }

  retryError(error: HttpErrorResponse, retryId: number, retryDelay: number) {
    // retry request with increasing delay between each attemp
    return of(error).pipe(delay(retryDelay * retryId));
  }
}
